import { useState } from "react";
import { DemoVoteSuccess } from "@/components/voting/demo-vote-success";
import { DemoChart } from "@/components/home/DemoChart";
import { useDemoEffects } from "./poll/use-demo-effects";
import { DemoPollHeader } from "./poll/demo-poll-header";
import { DemoSubmitButton } from "./poll/demo-submit-button";
import { demoPlayers } from "./types/demo-types";
import { triggerConfetti } from "@/utils/effects";
import { DemoPlayerList } from "@/components/voting/demo-player-list";
import { SelectedPlayersPodium } from "@/components/voting/selected-players-podium";
import { playButtonDownSound, playButtonUpSound } from "@/utils/effects";

export const DemoPoll = () => {
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [hasVoted, setHasVoted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Initialize effects
  const { primeEffects } = useDemoEffects();

  const handlePlayerSelect = (playerId: string) => {
    // Prime effects on selection to ensure they're ready
    primeEffects();
    
    setSelectedPlayers(prev => {
      // If empty string passed, clear selections
      if (playerId === "") {
        return [];
      }
      
      // If already selected, remove it from selections
      if (prev.includes(playerId)) {
        const newSelection = prev.filter(id => id !== playerId);
        return newSelection;
      }
      
      // If we already have 3 selections, replace the last one
      if (prev.length >= 3) {
        const newSelection = [...prev];
        newSelection[2] = playerId;
        return newSelection;
      }
      
      // Otherwise add to selections
      return [...prev, playerId];
    });
  };

  const handleSubmit = () => {
    if (selectedPlayers.length !== 3) return;
    
    setSubmitting(true);
    playButtonDownSound();
    
    // One more prime right before triggering to ensure everything is ready
    primeEffects();
    
    // Short timeout to ensure UI state update before effects
    setTimeout(() => {
      // Trigger confetti immediately for better timing
      triggerConfetti();
      playButtonUpSound();
      
      // Delayed state changes to match with visual effects
      setTimeout(() => {
        setHasVoted(true);
        setSubmitting(false);
      }, 500);
    }, 10);
  };

  const resetDemo = () => {
    setSelectedPlayers([]);
    setHasVoted(false);
    // Prime effects again when resetting to ensure they're ready for the next demo
    primeEffects();
  };

  return (
    <div className="max-w-md mx-auto my-8">
      <DemoPollHeader />

      {hasVoted ? (
        <DemoVoteSuccess onReset={resetDemo} />
      ) : (
        <>
          {/* Using the same component as the real vote experience for consistency */}
          <SelectedPlayersPodium 
            selectedPlayers={selectedPlayers} 
            players={demoPlayers} 
          />

          {/* Player Selection List */}
          <DemoPlayerList
            demoPlayers={demoPlayers}
            selectedPlayers={selectedPlayers}
            onPlayerSelect={handlePlayerSelect}
          />

          {/* Submit Button */}
          <DemoSubmitButton 
            selectedPlayers={selectedPlayers}
            submitting={submitting}
            onSubmit={handleSubmit}
          />
        </>
      )}
      
      {/* Demo Chart (always visible) */}
      <DemoChart />
    </div>
  );
};
